import React from "react";
import { VISIBLE_WHEN_OPERATORS, OPERATOR_LABELS } from "constants/ticketTemplates";

/**
 * VisibleWhenEditor (SOUND-506) — conditional-visibility predicate builder for
 * an intake field: `field (earlier only) · operator · value`. The field
 * dropdown offers ONLY fields above this one (same earlier-only rule as the
 * flow), keeping visibility resolution single-pass and acyclic. The referenced
 * field is stored by identity (`fieldId`) so it survives key renames.
 *
 * `value` shape (UI-internal): { fieldId, operator, value } | null.
 *
 * SOUND-546 reuses it for a workflow step's `spawnWhen` (same predicate shape,
 * same evaluator). The step passes ALL of the template's intake fields as
 * `earlierFields` and its own copy via `toggleLabel` / `leadText` / `emptyHint`;
 * the defaults are the intake-field wording.
 */
export default function VisibleWhenEditor({
  earlierFields = [],
  value,
  onChange,
  toggleLabel = "Conditional visibility",
  leadText = "Show only when",
  emptyHint = "(needs an earlier field)",
}) {
  const enabled = !!value;
  const set = (patch) => onChange({ ...(value || { operator: "eq", value: "" }), ...patch });
  const isMulti = value && (value.operator === "in" || value.operator === "notIn");
  // The referenced field's TYPE decides what kind of value input to show.
  const refField = value ? earlierFields.find((f) => f._id === value.fieldId) : null;
  const refIsBoolean = refField?.type === "boolean";

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          disabled={earlierFields.length === 0}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { fieldId: earlierFields[0]?._id || "", operator: "eq", value: "" }
                : null
            )
          }
          className="h-4 w-4 rounded border-border text-brand focus:ring-brand/40 disabled:opacity-50"
        />
        <span className="font-medium text-foreground">{toggleLabel}</span>
        {earlierFields.length === 0 && (
          <span className="text-xs text-muted-foreground">{emptyHint}</span>
        )}
      </label>

      {enabled && (
        <div className="flex flex-wrap items-center gap-2 pl-6">
          <span className="text-xs text-muted-foreground">{leadText}</span>
          <select
            className="select h-9 w-auto text-sm"
            value={value.fieldId || ""}
            onChange={(e) => {
              // Re-point the predicate. If the new target's booleanness differs,
              // the existing value is now the wrong TYPE (a leftover `true`
              // against a text field would never match), so clear it.
              const next = earlierFields.find((f) => f._id === e.target.value);
              const nextIsBoolean = next?.type === "boolean";
              set({
                fieldId: e.target.value,
                ...(nextIsBoolean !== refIsBoolean ? { value: "" } : {}),
              });
            }}
          >
            {earlierFields.map((f) => (
              <option key={f._id} value={f._id}>
                {f.label || f.key || "field"}
              </option>
            ))}
          </select>
          <select
            className="select h-9 w-auto text-sm"
            value={value.operator}
            onChange={(e) => set({ operator: e.target.value })}
          >
            {VISIBLE_WHEN_OPERATORS.map((op) => (
              <option key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </option>
            ))}
          </select>
          {/* SOUND-573: the value input must match the REFERENCED field's type.
              It used to be free text always — so a condition on a Yes/No field
              stored the STRING "true" while the field's value is a real boolean,
              and evaluateVisibleWhen compares with ===, making the predicate
              silently always-false and the dependent field permanently hidden.
              For a boolean reference, offer Yes/No and store real booleans.
              (Verified: zero visibleWhen predicates exist in prod, so this is
              forward-looking with no migration.) */}
          {refIsBoolean && !isMulti ? (
            <select
              className="select h-9 w-auto text-sm"
              value={value.value === true ? "true" : value.value === false ? "false" : ""}
              onChange={(e) =>
                set({ value: e.target.value === "" ? "" : e.target.value === "true" })
              }
            >
              <option value="">Select…</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : (
            <input
              className="input h-9 min-w-[140px] flex-1 text-sm"
              placeholder={isMulti ? "comma, separated, values" : "value"}
              value={value.value ?? ""}
              onChange={(e) => set({ value: e.target.value })}
            />
          )}
        </div>
      )}
    </div>
  );
}
